// Base API client utilities

// Cached API base URL - resolved lazily for testability
let _apiBaseUrl: string | null = null;

function getApiBaseUrl(): string {
	if (_apiBaseUrl !== null) {
		return _apiBaseUrl;
	}
	const url = import.meta.env.VITE_API_URL || "http://localhost:8001";
	_apiBaseUrl = url;
	return url;
}

// For backwards compatibility - use getApiBaseUrl() for new code
export const API_BASE_URL =
	import.meta.env.VITE_API_URL || "http://localhost:8001";

export type RequestOptions = {
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	body?: unknown;
	headers?: Record<string, string>;
	signal?: AbortSignal;
};

export type ApiResponse<T> = {
	success: boolean;
	data?: T;
	error?: {
		code: string;
		message: string;
	};
	meta?: {
		total?: number;
		page?: number;
		limit?: number;
		totalPages?: number;
		hasNext?: boolean;
		hasPrev?: boolean;
	};
};

export class ApiError extends Error {
	constructor(
		public code: string,
		message: string,
		public status: number,
	) {
		super(message);
		this.name = "ApiError";
	}
}

/** User-friendly fallback message for unexpected errors */
export const GENERIC_ERROR_MESSAGE =
	"We're sorry, this could not be completed. Please write to us at hi@interioring.com and we'll get back to you.";

/** Extract a user-friendly error message from any error */
export function getErrorMessage(err: unknown): string {
	if (err instanceof ApiError) {
		return err.message;
	}
	return GENERIC_ERROR_MESSAGE;
}

export async function request<T>(
	endpoint: string,
	options: RequestOptions = {},
): Promise<ApiResponse<T>> {
	const { method = "GET", body, headers = {}, signal } = options;

	const config: RequestInit = {
		method,
		headers: {
			"Content-Type": "application/json",
			...headers,
		},
		credentials: "include", // Include cookies for session
		signal,
	};

	if (body) {
		config.body = JSON.stringify(body);
	}

	const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

	// Check if response is actually JSON before parsing
	const contentType = response.headers.get("content-type");
	if (!contentType || !contentType.includes("application/json")) {
		const textResponse = await response.text();
		console.error("Non-JSON response from API:", {
			endpoint,
			status: response.status,
			contentType,
			responsePreview: textResponse.substring(0, 200),
		});
		throw new ApiError(
			"INVALID_RESPONSE",
			GENERIC_ERROR_MESSAGE,
			response.status,
		);
	}

	let data: ApiResponse<T>;
	try {
		data = await response.json();
	} catch (jsonError) {
		console.error("Failed to parse JSON response:", {
			endpoint,
			error: jsonError instanceof Error ? jsonError.message : String(jsonError),
		});
		throw new ApiError(
			"PARSE_ERROR",
			GENERIC_ERROR_MESSAGE,
			response.status,
		);
	}

	if (!response.ok || !data.success) {
		throw new ApiError(
			data.error?.code || "UNKNOWN_ERROR",
			data.error?.message || "An error occurred",
			response.status,
		);
	}

	return data;
}

// Helper to get full image URL from relative path
export function getImageUrl(path: string): string {
	if (!path) return "";
	if (path.startsWith("http://") || path.startsWith("https://")) {
		return path;
	}
	const imageBaseUrl =
		import.meta.env.VITE_IMAGE_URL || `${getApiBaseUrl()}/api/images`;
	// If path starts with /api/images, strip the prefix and use the image base URL
	if (path.startsWith("/api/images/")) {
		const storageKey = path.replace("/api/images/", "");
		return `${imageBaseUrl}/${storageKey}`;
	}
	// Otherwise assume it's a relative path and construct the full URL
	return `${imageBaseUrl}/${path}`;
}

/**
 * Upload a file to the API
 *
 * @param endpoint - API endpoint path (e.g., "/api/pro/123/projects/456/upload-photo")
 * @param file - File to upload
 * @param additionalFields - Optional additional form fields to include
 * @returns API response with the uploaded resource
 */
export async function uploadFile<T>(
	endpoint: string,
	file: File,
	additionalFields?: Record<string, string>,
	options?: { signal?: AbortSignal },
): Promise<ApiResponse<T>> {
	const formData = new FormData();
	formData.append("file", file);

	if (additionalFields) {
		for (const [key, value] of Object.entries(additionalFields)) {
			formData.append(key, value);
		}
	}

	const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
		method: "POST",
		credentials: "include",
		body: formData,
		signal: options?.signal,
	});

	let data: ApiResponse<T>;
	try {
		data = await response.json();
	} catch {
		throw new ApiError("PARSE_ERROR", GENERIC_ERROR_MESSAGE, response.status);
	}
	if (!response.ok || !data.success) {
		throw new ApiError(
			data.error?.code || "UPLOAD_ERROR",
			data.error?.message || "Failed to upload file",
			response.status,
		);
	}

	return data as ApiResponse<T>;
}
